import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

/**
 * Světlý/tmavý režim (E11-US7). Volba je uživatelská preference napříč
 * projekty (localStorage), ne součást projektového souboru — stejný vzor
 * jako `Language` v `i18n/index.tsx`. Aplikuje se přes `data-theme` na
 * `<html>`, který `styles.css` (Epic 6/pozadavky.md kap. 6) už umí přebít.
 */
export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'bits2schema.theme';

interface ThemeContextValue {
  theme: Theme;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function readInitialTheme(): Theme {
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === 'light' || stored === 'dark') return stored;
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function applyTheme(theme: Theme) {
  document.documentElement.dataset.theme = theme;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>(() => {
    const initial = readInitialTheme();
    applyTheme(initial);
    return initial;
  });

  const toggleTheme = useCallback(() => {
    setTheme((previous) => {
      const next: Theme = previous === 'dark' ? 'light' : 'dark';
      window.localStorage.setItem(STORAGE_KEY, next);
      applyTheme(next);
      return next;
    });
  }, []);

  const value = useMemo<ThemeContextValue>(() => ({ theme, toggleTheme }), [theme, toggleTheme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme musí být uvnitř ThemeProvider.');
  return context;
}
